import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { isEmpty } from "../../../utils/Util";
import { TargetPayApi } from "../../../utils/UtilTarget";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { IDealerLocation, IDealerLocationGroup } from "../../../types/types";
import { HttpStatusCode } from "axios";

export default function AssignLocationGroup() {
  const location = useLocation();
  const dealerData = location?.state?.DealerSummary;
  const dmsDealerId = dealerData?.dmsDealerId;
  const companyDealerId = dealerData?.companyDealerId;

  const [dealerLocations, setDealerLocations] = useState<IDealerLocation[]>([]);
  const [originalDealerLocations, setOriginalDealerLocations] = useState<
    IDealerLocation[]
  >([]);
  const [dealerLocationGroups, setDealerLocationGroups] = useState<
    IDealerLocationGroup[]
  >([]);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [validationErrors, setValidationErrors] = useState<number[]>([]); // List of DealerIds with missing group

  useEffect(() => {
    document.title = "Setup Location Group";
    if (isEmpty(dmsDealerId)) return;

    const fetchData = async () => {
      try {
        setIsFetchingData(true);

        await Promise.all([
          fetchDealerLocations(),
          fetchDealerLocationGroups(),
        ]);
      } finally {
        setIsFetchingData(false);
      }
    };

    fetchData();
  }, [dmsDealerId]);

  // Fetch Dealer Locations
  const fetchDealerLocations = async () => {
    const res = await callQuery("GetDealersByDmsDealerId");
    setDealerLocations(res || []);
    setOriginalDealerLocations(res || []); // keep original for comparison
  };

  // Fetch Dealer Location Groups
  const fetchDealerLocationGroups = async () => {
    const res = await callQuery("GetDealerLocationGroups");
    setDealerLocationGroups(res || []);
  };

  const callQuery = async (query: string) => {
    return await TargetPayApi(
      `${query}/${dmsDealerId}/${companyDealerId}`,
      null,
      "get",
      "Dealer",
    );
  };

  const handleGroupChange = (
    dealerId: number,
    selectedGroup: IDealerLocationGroup | null,
  ) => {
    setDealerLocations((prev) =>
      prev.map((loc) =>
        loc.dealerId === dealerId
          ? { ...loc, locationGroupId: selectedGroup?.locationGroupId ?? 0 }
          : loc,
      ),
    );

    // Remove from error list if group was previously missing
    if (selectedGroup) {
      setValidationErrors((prev) => prev.filter((id) => id !== dealerId));
    }
  };

  const handleSave = async () => {
    const missingGroups = dealerLocations
      .filter((loc) => loc.locationGroupId === 0)
      .map((loc) => loc.dealerId);

    if (missingGroups.length > 0) {
      setValidationErrors(missingGroups);
      return;
    }

    // Find changed locations
    const changedLocations = dealerLocations.filter((loc) => {
      const original = originalDealerLocations.find(
        (o) => o.dealerId === loc.dealerId,
      );
      return original && original.locationGroupId !== loc.locationGroupId;
    });

    if (changedLocations.length === 0) {
      toast.info("No location group changes detected.", {
        position: toast.POSITION.TOP_CENTER,
        style: { width: "350px" },
      });
      return; // prevent save if you only want to save when changes exist
    }

    const dealerRequest = dealerLocations.map((loc) => ({
      DealerId: loc.dealerId,
      CompanyDealerId: companyDealerId,
      DmsDealerId: dmsDealerId,
      LocationGroupId: loc.locationGroupId,
      IsDealerOnboarded: loc.isDealerOnboarded,
    }));

    setIsAssigning(true);

    try {
      const res = await TargetPayApi(
        `AssingLocationGroup?dmsDealerId=${dmsDealerId}&companyDealerId=${companyDealerId}`,
        dealerRequest,
        "post",
        "Dealer",
      );

      if (res) {
        toast.dismiss();
        toast.success("Location Groups assigned successfully.", {
          position: toast.POSITION.TOP_CENTER,
          style: { width: "380px" },
        });

        // Update the baseline after saving
        setOriginalDealerLocations(dealerLocations);
      }
    } catch (error) {
      toast.error(error.reasonPhrase || "An unexpected error occurred.", {
        position: toast.POSITION.TOP_CENTER,
        style: { width: "470px" },
      });

      if (error?.status === HttpStatusCode.BadRequest && error.data?.errors) {
        Object.values(error.data.errors)
          .flat()
          .forEach((message) => toast.error(`${message}`, { autoClose: 5000 }));
      }
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Box>
      {isFetchingData ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={25} />
        </Box>
      ) : (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            {isAssigning && (
              <CircularProgress
                size={25}
                style={{
                  marginRight: "20px",
                  marginTop: "5px",
                }}
                color="info"
              />
            )}
            <Button
              variant="contained"
              disabled={isAssigning || dealerLocationGroups.length === 0}
              color="primary"
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>

          <Paper>
            <TableContainer sx={{ height: "30vh", overflow: "auto" }}>
              <Table
                size="small"
                className="sticky-header"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Dealer Id</TableCell>
                    <TableCell>Dms Dealer Id</TableCell>
                    <TableCell>Dealer Name</TableCell>
                    <TableCell>Location Group</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealerLocations.map((row: any) => {
                    const selectedGroup =
                      dealerLocationGroups.find(
                        (group) =>
                          group.locationGroupId === row.locationGroupId,
                      ) || null;

                    const hasError = validationErrors.includes(row.dealerId);

                    return (
                      <TableRow key={row.dealerId}>
                        <TableCell>{row.dealerId}</TableCell>
                        <TableCell>{row.dmsDealerId}</TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            autoHighlight
                            fullWidth
                            disabled={row.isDealerOnboarded}
                            options={dealerLocationGroups}
                            value={selectedGroup}
                            getOptionLabel={(option) => `${option.name}`}
                            sx={{
                              "& .MuiAutocomplete-input, & .MuiAutocomplete-option":
                                {
                                  fontSize: "14px",
                                },
                            }}
                            renderInput={(params) => (
                              <TextField
                                size="small"
                                variant="standard"
                                {...params}
                                InputProps={{
                                  ...params.InputProps,
                                  sx: { fontSize: "14px" },
                                }}
                                error={hasError}
                                helperText={
                                  hasError ? "Location group is missing" : ""
                                }
                              />
                            )}
                            onChange={(event, value) => {
                              handleGroupChange(row.dealerId, value);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
